import {
  Button,
  EButtonColorVariant,
  ELinkColorVariant,
  ELinkVariant,
  ETextStyleVariant,
  EToastVariant,
  Link,
  Text,
} from '@outdoorsyco/bonfire';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ListingAmenities } from '@/components/route/campground/BookASite/ListingAmenities';
import Rating from '@/components/route/listing/Rating/Rating';
import { PriceDetails } from '@/components/ui/ListingTile/PriceDetails';
import { triggerOpenSearchHeaderDates } from '@/components/ui/SearchHeader/NavbarSearch/events';
import { AMENITIES_ICONS } from '@/constants/amenities';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRouter } from '@/hooks/useRouter';
import { useToast } from '@/hooks/useToast';
import { clearQuote, getQuote } from '@/redux/modules/quote';
import { getCampgroundListingData } from '@/redux/selectors/campgrounds/page';
import { PriceType } from '@/redux/selectors/listing/page';
import { getFromAndTo } from '@/redux/selectors/queryParams';
import { getQuoteId, getQuoteIsLoading } from '@/redux/selectors/quote';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { IDeliveryQueryParams } from '@/services/types/core/quotes';
import { ICampgroundData } from '@/services/types/search/campgrounds/id';
import { getStateName, provinceByCountry, TCountry } from '@/utility/location';
import { mapAmenities } from '@/utility/mapAmenities';
import { IRentalTile } from '@/utility/mapSearchResultToTile';
import { useThunkDispatch } from '@/utility/redux/dispatch';

import { ListingPhotos } from './ListingPhotos';

interface IBundledListingsTileProps {
  rv: IRentalTile;
  wishlistingEventData?: IWishlistingEventData;
  scrollToSearchFilter?: () => void;
}

const mapCampgroundDataToQueryParams = (
  campgroundData: ICampgroundData | null,
): IDeliveryQueryParams | null => {
  if (!campgroundData) return null;

  const { lng, lat, street, city, state, zip, country } = campgroundData.location;

  const formattedCountry = (country?.toUpperCase() as TCountry) || 'US';

  const stateName = getStateName(state, formattedCountry) ?? state;
  const countryName = provinceByCountry[formattedCountry].name;
  const address = `${street}, ${city}, ${stateName} ${zip}, ${countryName}`;

  return {
    deliveryCampgroundId: campgroundData.id,
    deliveryAddress: address,
    delivery: true,
    deliveryStationary: EDeliveryOption.STATIONARY,
    deliveryCenter: { lng, lat },
    deliveryDetails: {
      country: countryName,
      state: stateName,
      city,
      street,
      zip,
      lat,
      lng,
      street_etc: '',
    },
  };
};

export const BundledListingsTile = ({
  rv,
  wishlistingEventData,
  scrollToSearchFilter,
}: IBundledListingsTileProps) => {
  const { sleepingLabel, id, details, price, currency, instantBook, catalog, slug, vehicleTitle } =
    rv;
  const { isMobile } = useBreakpoint();
  const intl = useIntl();
  const dates = useSelector(getFromAndTo);

  const quoteId = useSelector(getQuoteId) || '';

  const handleOpenSearchHeaderDates = () => {
    triggerOpenSearchHeaderDates();
    if (!isMobile) {
      scrollToSearchFilter?.();
    }
  };

  const showSelectDatesBtn = !dates?.from || !dates?.to;
  const showBookNowBtn = dates?.from && dates?.to;
  const { sleeps, ratingNumber, ratingQtd } = details || {};
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingQuote = useSelector(getQuoteIsLoading);
  const campground = useSelector(getCampgroundListingData);
  const deliveryQueryParams = useMemo(
    () => mapCampgroundDataToQueryParams(campground),
    [campground],
  );

  const sleepingInfo = [sleepingLabel, sleeps].filter(Boolean).join(`  •  `);
  const { showToast } = useToast();

  const dispatch = useThunkDispatch();
  const router = useRouter();

  const redirectToCheckout = useCallback(
    (newQuoteId?: string) => {
      router.push(`/checkout/${id ?? ''}/new?quote=${newQuoteId ?? quoteId ?? ''}`);
    },
    [quoteId, id, router],
  );

  const rvAmenities = useMemo(() => {
    const { amenities_categories, processed_amenities } = catalog || {};
    if (!amenities_categories || !processed_amenities) {
      return {
        categories: [],
        list: [],
      };
    }

    return mapAmenities(processed_amenities, amenities_categories, AMENITIES_ICONS);
  }, [catalog]);

  const handleBookNow = async () => {
    try {
      setIsLoading(true);
      if (quoteId) {
        dispatch(clearQuote());
      }

      const dateFrom = dates?.from ? new Date(dates.from) : new Date();
      const dateTo = dates?.to ? new Date(dates.to) : new Date();

      const quoteData = await dispatch(
        getQuote({
          rentalId: id,
          throwOnFetchFailure: true,
          ...(deliveryQueryParams ? { deliveryQueryParams } : {}),
          additionalEventData: {
            isEscape: true,
            campgroundAddress: `${campground?.location.city}, ${campground?.location.state}`,
            campgroundName: campground?.name,
            campgroundCheckInTime: campground?.check_in_time,
            campgroundCheckOutTime: campground?.check_out_time,
            totalNights: dayjs(dateFrom).diff(dateTo, 'day'),
            departureDate: dateFrom.getTime(),
            returnDate: dateTo.getTime(),
          },
        }),
      );

      if (quoteData?.id) {
        redirectToCheckout(quoteData.id);
        return;
      }
    } catch (errorMessage) {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({ defaultMessage: 'Booking Error', id: 'U8/IW1' }),
        errorMessage ||
          intl.formatMessage({
            defaultMessage: 'This rv suite is no longer available for the selected dates.',
            id: 'ivZruS',
          }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="grid gap-4 grid-cols-1">
        <ListingPhotos rental={rv} wishlistingEventData={wishlistingEventData} enhanceImages />

        <div>
          <div className="flex justify-between gap-4">
            <Text
              variant={ETextStyleVariant.LargeBold}
              className="md:overflow-hidden md:text-ellipsis md:whitespace-nowrap">
              {vehicleTitle ? vehicleTitle : sleepingInfo}
            </Text>

            {!!ratingNumber && !!ratingQtd && (
              <div className="flex items-center">
                <Rating
                  className="mr-1 text-sm font-bold text-gray-800 semiHighlight"
                  number={ratingNumber}
                />

                <Link
                  colorVariant={ELinkColorVariant.Secondary}
                  href={`${slug}/reviews`}
                  label={`(${ratingQtd})`}
                  variant={ELinkVariant.NoUnderline}
                  className="ml-1"
                  target="_blank"
                />
              </div>
            )}
          </div>

          {!!vehicleTitle && (
            <Text
              variant={ETextStyleVariant.MediumBold}
              className="my-2 md:overflow-hidden md:text-ellipsis md:whitespace-nowrap">
              {sleepingInfo}
            </Text>
          )}

          <div className="my-2">
            <ListingAmenities
              amenities={rvAmenities}
              colorVariant={ELinkColorVariant.Secondary}
              isRVBundle={true}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <PriceDetails
            currency={currency}
            instantBook={instantBook}
            originalPrice={price?.vacationPackageNightlyRate || 0}
            currentPrice={price?.vacationPackageNightlyRate || 0}
            period={PriceType.NIGHT}
            showDiscount={false}
            isRVBundle
          />

          {!!showSelectDatesBtn && (
            <Button
              label={intl.formatMessage({ defaultMessage: 'Select dates', id: 'cBCa5I' })}
              onClick={handleOpenSearchHeaderDates}
              variant={EButtonColorVariant.Secondary}
            />
          )}
          {!!showBookNowBtn && (
            <Button
              label={intl.formatMessage({ defaultMessage: 'Book now', id: '1mLe+3' })}
              onClick={handleBookNow}
              variant={EButtonColorVariant.Primary}
              loading={isLoading && isLoadingQuote}
              disabled={isLoadingQuote}
            />
          )}
        </div>
      </div>
    </>
  );
};
